import { SellDeliveryNoteProductModelTypeExtended } from '@teinor/erp/types/company/order/sell/sellDeliveryNote/sellDeliveryNoteProduct'
import { useMemo } from 'react'
import { permissionCheck } from '../../../../../../../../../../baseComponents/PermissionChecker/function'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../../specificComponents/Product/types'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import OrderSellLayout from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../../utils/globals/dictionary'
import useOrderRowRenderer from '../../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import useSellDeliveryNoteHeaderUrls from '../../../../../../../../../../utils/hooks/sells/orders/useSellDeliveryNoteHeaderUrls'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkingCenters, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import SellDeliveryNoteWaitingResponseHeader from './SellDeliveryNWaitRespITHeader'
import { sellDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import SellDeliveryNoteWaitingResponseStepHeaderDecider from './SellDeliveryNWaitRespHeader'

type SellDeliveryNoteWaitingResponseProps = InputTableLayoutStepProps & { 
}

const SellDeliveryNoteWaitingResponse: React.FC<SellDeliveryNoteWaitingResponseProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useSellDeliveryNoteWaitingResponseLogic({ completeData })
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    const orderHeaderURLs = useSellDeliveryNoteHeaderUrls()
    if (!completeData) {
        return null
    }
    const workingCenters = getAllWorkingCenters()
    const dictWarehouses = workingCenters?.[completeData?.header?.WorkingCenter?.id]?.dictWarehouses
    const statusColumn: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = []
    if (completeData?.header.WorkSheetId || completeData?.header?.relatedWorkSheets?.length) {
        statusColumn.push(
            {
                id: 'haveWorkSheetProductStatus',
                dataKey: 'haveWorkSheetProductStatus',
                label: '',
                simpleLabel: t('workSheetStatus', 'Estado hoja de trabajo'),
                component: 'OrderProductWorkSheetStatus',
                hideColumnByDefault: true,
                Order: completeData?.header || {}
            },
        )
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = [
        {
            id: 'selected',
            dataKey: 'selected',
            label: '',
            simpleLabel: t('selectLine', 'Seleccionar línea'),
            width: 50,
            requiredColumn: true,
            component: 'ITCheckbox',
            headerClassName: 'checkbox_header_filter',
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: { 'unprocessed': { name: t('unprocessed', 'Sin procesar') }, 'processed': { name: t('processed', 'Procesados') } }, label: t('show', 'Mostrar') },
            }
        },
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
            minWidth: 115
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className='twin_elipsis' />
        },
        {
            id: 'SpecificPriceRuleId',
            dataKey: 'SpecificPriceRuleId',
            label: t('priceRule', 'Tarifa'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={'T' + allRowData?.['SpecificPriceRule.id'] + ' - ' + allRowData?.['SpecificPriceRule.name']} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
            simpleLabel: t('price', 'Precio'),
            component: 'ITDisplayPrice',
            width: 100,
        },
        {
            id: 'manualPrice',
            dataKey: 'manualPrice',
            label: t('manualPriceS', 'P. man.'),
            simpleLabel: t('manualPrice', 'Precio manual'),
            toolTip: t('manualPrice', 'Precio manual'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={60} />,
            hideColumnByDefault: true,
            width: 85,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantityServedShort2', 'Cant. serv.'),
            width: 110,
            component: 'ITInputQuantity',
            typeOnChange: 'onChangeTotalsFetch',
            simpleLabel: t('quantityServed', 'Cantidad servida'),
            toolTip: t('quantityServed', 'Cantidad servida'),
            skipMinQuantity: true
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 110,
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'OrderAccountId',
            dataKey: 'OrderAccountId',
            label: t('chapter', 'Capítulo'),
            customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'desiredQuantity',
            dataKey: 'desiredQuantity',
            label: t('desiredQuantityShort', 'Cant. des.'),
            simpleLabel: t('desiredQuantity', 'Cantidad deseada'),
            toolTip: t('desiredQuantity', 'Cantidad deseada'),
            width: 105,
            component: 'ITInputQuantity',
            readOnlyFnc: () => true
        },
        {
            id: 'processed',
            dataKey: 'processed',
            component: 'OrderSaveRemainingProduct',
            label: t('invoiced', 'Facturado'),
            width: 110,
            forceNullBoolean: true
        },
        {
            id: 'discount',
            dataKey: 'discount',
            label: t('discount', 'Descuento'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
            simpleLabel: t('rowPrice', 'Precio Línea'),
            component: 'ITDisplayFinalPrice',
            hideColumnByDefault: true,
            with_vat: false,
            width: 120
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            component: 'ITOrderWarehouse',
            hideColumnByDefault: true,
            warehouses: dictWarehouses || []
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
        ...statusColumn
    ]
    return (
        <OrderSellLayout modelId='SellDeliveryNoteId' name='SellDeliveryNoteWaitingResponse' translations={{ headerTitle: t('deliveryNote', 'Albarán') }} buttonSave={
            permissionCheck(getUserPermissions('sell.deliveryNote'), 'update') || permissionCheck(getUserPermissions('sell.deliveryNote'), 'delete') ?
                <SellDeliveryNoteWaitingResponseStepHeaderDecider completeData={completeData} onSubmit={getCompleteData} isReadOnly={true} data={completeData as OrderData} />
                : undefined
        } InputTableHeaderChildren={SellDeliveryNoteWaitingResponseHeader} isReadOnly={true} columns={columns} {...{ orderHeaderURLs, rowRenderer, inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges, orderStatusDict: sellDeliveryNoteStatusInversed }} />
    )
}

interface SellDeliveryNoteWaitingResponseLogicProps {
    completeData: OrderData | null
}

export const useSellDeliveryNoteWaitingResponseLogic = ({ completeData }: SellDeliveryNoteWaitingResponseLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData })
    return { rowRenderer }
}

export default SellDeliveryNoteWaitingResponse